/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { getCompanyByWebContext, fetchPlansByCompanyId } from "../../http/index";
import { saveSbsSubscriberData } from "../../Redux/B2CSubscriber-Slice/Subscriber-sbs-slice";
import "./ServicesLanding.css";

gsap.registerPlugin(ScrollTrigger);

interface PlanData {
  planName: string;
  planCategory: string;
  amount: string;
  period: string;
  coverageTitle: string;
  coverageDescription: string;
  highlights: string[];
  includesText: string;
  themeColor: string;
  planId: string;
}

const ServicesLanding: React.FC = () => {
  const { webContext } = useParams<{ webContext: string }>();
  const rootRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const beneficiaries = useSelector((state: any) => state.beneficiary);
  const userData = useSelector((state: any) => state.user);
  const [individualPlan, setIndividualPlan] = useState<PlanData | null>(null);
  const [familyPlan, setFamilyPlan] = useState<PlanData | null>(null);

  // Save webContext to redux so PrivateRoute can use it for redirection
  useEffect(() => {
    if (webContext) {
      dispatch(saveSbsSubscriberData({ webContext }));
    }
  }, [webContext]); // eslint-disable-line

  const handleBuyNow = (planId: string) => {
    if (userData && Object.keys(userData).length > 0) {
      navigate(`/subscription?planId=${planId}`);
    } else {
      window.location.href = `https://customer.pococare.com/registration?context=edme&planId=${planId}`;
    }
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["partnerCompany", webContext],
    queryFn: () => getCompanyByWebContext((webContext ?? "").toLowerCase()),
    retry: false,
  });

  const partner = data?.data?.data?.[0];

  useEffect(() => {
    if (!partner?._id) return;
    const fetchPlans = async () => {
      try {
        const res = await fetchPlansByCompanyId(partner._id);
        const plans: PlanData[] = res.data;
        setIndividualPlan(plans.find((p) => p.planCategory === "Individual") ?? null);
        setFamilyPlan(plans.find((p) => p.planCategory === "Family") ?? null);
      } catch (error) {
        console.error("Error fetching plans:", error);
      }
    };
    fetchPlans();
  }, [partner?._id]);

  const handleAssistanceClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (beneficiaries?.length > 0) {
      navigate(`/trigger-emergency/${beneficiaries[0]._id}?source=sso`);
    }
  };

  /* ── View-more toggle state ── */
  const toggleViewMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    const btn = e.currentTarget;
    const list = btn.previousElementSibling as HTMLElement | null;
    if (!list) return;
    if (list.classList.contains("open")) {
      list.classList.remove("open");
      btn.textContent = "View More +";
    } else {
      list.classList.add("open");
      btn.textContent = "View Less -";
    }
  };

  /* ── GSAP animations (mirrors custom.js exactly) ── */
  useEffect(() => {
    if (typeof window === "undefined" || !partner) return;

    /* scroll restoration – match custom.js */
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);

    if (window.innerWidth <= 991) return;

    /* wait one frame so browser has painted full page height */
    const initId = requestAnimationFrame(() => {
      const ctx = gsap.context(() => {
        /* paraAnimation – fade up */
        document.querySelectorAll<HTMLElement>(".sl-root .paraAnimation").forEach((el) => {
          gsap.from(el, {
            scrollTrigger: el,
            opacity: 0,
            y: 50,
            duration: 1,
          });
        });

        /* reveal-img */
        document.querySelectorAll<HTMLElement>(".sl-root .reveal-img").forEach((container) => {
          const image = container.querySelector("img");
          const tl = gsap.timeline({
            scrollTrigger: { trigger: container },
          });
          tl.set(container, { autoAlpha: 1 });
          tl.from(container, 1.5, { xPercent: -100, ease: "power2.out" });
          if (image) {
            tl.from(image, 1.5, { xPercent: 100, delay: -1.5, ease: "power2.out" });
          }
        });

        /* appearance animations */
        const appear = (selector: string, trigger: string, direction: "top" | "left" = "top") => {
          const els = document.querySelectorAll<HTMLElement>(selector);
          if (!els.length) return;
          gsap.from(els, {
            scrollTrigger: { trigger: document.querySelector(trigger) ?? undefined },
            ...(direction === "left" ? { x: -100 } : { y: -100 }),
            stagger: 0.2,
            duration: 1,
            opacity: 0,
          });
        };

        appear(".sl-root .plan-section .pococare-card", ".sl-root .plan-section .row");
        appear(".sl-root .why-col.col-right .row > div", ".sl-root .why-col.col-right");
        appear(".sl-root .why-col.col-left .row > div", ".sl-root .why-col.col-left");
        appear(".sl-root .not-included .services-list li", ".sl-root .not-included");
        appear(".sl-root .included .services-list li", ".sl-root .included");
        appear(".sl-root .how-works .step-box", ".sl-root .how-works .row", "left");
        appear(".sl-root .why-choose .row > div", ".sl-root .why-choose .row");

        ScrollTrigger.refresh();
      }, rootRef);

      return () => ctx.revert();
    });

    return () => {
      cancelAnimationFrame(initId);
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, [partner]);

  useEffect(() => {
    if (!individualPlan || !familyPlan || window.innerWidth <= 991) return;
    const id = requestAnimationFrame(() => {
      const cards = document.querySelectorAll<HTMLElement>(
        ".sl-root .plan-section .pococare-card.indivisual-plan, .sl-root .plan-section .pococare-card.family-plan"
      );
      if (!cards.length) return;
      gsap.from(cards, { y: -100, stagger: 0.2, duration: 1, opacity: 0 });
    });
    return () => cancelAnimationFrame(id);
  }, [individualPlan, familyPlan]);

  if (isLoading) return <div className="sl-root" style={{ minHeight: "100vh" }} />;
  if (isError || !partner) return <Navigate to="/companyNotFound" replace />;

  /* ── run animations only after partner data is ready ── */
  // (useEffect above depends on [partner] via the key below)

  return (
    <div className="sl-root" ref={rootRef}>
      {/* ── HEADER ── */}
      <header className="top-header">
        <div className="container-fluid">
          <div className="logo-wrap">
            <a href="https://pococare.com/" className="poco-logo">
              <img src="https://pococare.com/wp-content/themes/pococare/assets/images/logo.png" alt="Pococare" />
            </a>
            <a href="#" className="partner-logo">
              <img src={partner.logo} alt={partner.companyName} />
            </a>
          </div>
        </div>
      </header>

      {/* ── PLAN SECTION ── */}
      <section className="plan-section">
        <div className="container">
          <h1 className="main-heading paraAnimation">
            When Every Minute Matters, <span>Be Ready.</span>
          </h1>
          <p className="heading-subtitle paraAnimation">
            Buy Pococare Subscription and be Medical emergency ready
          </p>
          <div className="row">
            {/* Assistance Plan */}
            <div className="col-md-4">
              <div className="pococare-card assistance-plan">
                <div className="card-content">
                  <img src="	https://pococare.com/edme/assets/images/icons/bell-icon.webp" className="icon mb-2" alt="" />
                  <h2 className="card-heading">
                    <strong className="color-purple">Assistance</strong>{" "}
                    <span className="fw-light">Plan</span>
                  </h2>
                  <div className="devider mx-auto color-purple-bg my-4"></div>
                  <h3>Assistance Charges Applicable</h3>
                  <p className="color-text-muted">
                    Doctor consultation, ambulance trip and platform access
                  </p>
                  <ul className="services-list">
                    <li>
                      <strong>
                        <span className="color-purple">Coverage</span>
                        <br />
                        Medical Emergency Incident Assistance
                      </strong>
                    </li>
                    <li>
                      <strong className="color-purple">Includes</strong>
                      <br />
                      24x7 Access to Pococare Assistance
                    </li>
                  </ul>
                </div>
                <a href="#" className="buynow-btn" onClick={handleAssistanceClick}>
                  Get Assistance Now
                </a>
              </div>
            </div>

            {/* Individual Plan */}
            {individualPlan && (
            <div className="col-md-4">
              <div className="pococare-card indivisual-plan">
                <div className="card-content">
                  <h2 className="card-heading">
                    <span className="fw-light">{individualPlan.planName}</span>
                    <br />
                    <strong className="color-teal" style={{ color: individualPlan.themeColor }}>{individualPlan.planCategory}</strong>
                  </h2>
                  <div className="devider mx-auto color-teal-bg my-4" style={{ backgroundColor: individualPlan.themeColor }}></div>
                  <div className="original-price">
                    ₹7,000 <small>per year</small>
                  </div>
                  <div className="discounted-price">
                    ₹{(Number(individualPlan.amount) / 100).toLocaleString()} <small>per year</small>
                  </div>
                  <ul className="services-list">
                    <li>
                      <strong>
                        <span className="color-teal" style={{ color: individualPlan.themeColor }}>Coverage: </span>
                        <br />
                        {individualPlan.coverageTitle}
                      </strong>
                    </li>
                    <li>
                      <strong className="color-teal" style={{ color: individualPlan.themeColor }}>
                        {individualPlan.includesText}
                      </strong>
                      <br />
                      {individualPlan.highlights[0]}
                    </li>
                  </ul>
                  <ul className="viewmore-services">
                    <li>Medical Emergency Readiness Profile</li>
                    <li>Secure Storage of Critical Information</li>
                    <li>Access via QR Code, Mobile App &amp; Helpline</li>
                    <li>Unlimited Doctor Consultations</li>
                    <li>
                      One Complimentary Ambulance Trip (up-to 10 km) Per Year
                      for medical emergency
                    </li>
                    <li>Emergency Coordination Support</li>
                  </ul>
                  <button className="viewmore-btn" onClick={toggleViewMore}>
                    View More +
                  </button>
                </div>
                <button
                  className="buynow-btn"
                  onClick={() => handleBuyNow(individualPlan.planId)}
                >
                  Buy Now
                </button>
              </div>
            </div>
            )}

            {/* Family Plan */}
            {familyPlan && (
            <div className="col-md-4">
              <div className="pococare-card family-plan">
                <div className="card-content">
                  <h2 className="card-heading">
                    <span className="fw-light">{familyPlan.planName}</span>
                    <br />
                    <strong className="color-pink" style={{ color: familyPlan.themeColor }}>{familyPlan.planCategory}</strong>
                  </h2>
                  <div className="devider mx-auto color-pink-bg my-4" style={{ backgroundColor: familyPlan.themeColor }}></div>
                  <div className="original-price">
                    ₹14,000 <small>per year</small>
                  </div>
                  <div className="discounted-price">
                    ₹{(Number(familyPlan.amount) / 100).toLocaleString()} <small>per year</small>
                  </div>
                  <ul className="services-list">
                    <li>
                      <strong>
                        <span className="color-pink" style={{ color: familyPlan.themeColor }}>Coverage: </span> {familyPlan.coverageTitle}
                      </strong>
                      <br />
                      {familyPlan.coverageDescription.split("\n").map((line, idx) => (
                        <React.Fragment key={idx}>
                          {idx > 0 && <br />}
                          {line}
                        </React.Fragment>
                      ))}
                    </li>
                    <li>
                      <strong className="color-pink" style={{ color: familyPlan.themeColor }}>
                        {familyPlan.includesText}
                      </strong>
                      <br />
                      {familyPlan.highlights[0]}
                    </li>
                  </ul>
                  <ul className="viewmore-services">
                    {familyPlan.highlights.slice(1).map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                  <button className="viewmore-btn" onClick={toggleViewMore}>
                    View More +
                  </button>
                </div>
                <button
                  className="buynow-btn"
                  onClick={() => handleBuyNow(familyPlan.planId)}
                >
                  Buy Now
                </button>
              </div>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* ── WHY POCOCARE ── */}
      <section className="why-pococare">
        <div className="container">
          <h2 className="global-heading paraAnimation">
            Why Buy <b>Pococare?</b>
          </h2>
          <div className="row gutter-lg">
            {/* Without Pococare */}
            <div className="col-md-6 why-col col-left">
              <div className="why-box without-poco">
                <h3 className="paraAnimation">Without Pococare</h3>
                <div className="row gy-4">
                  {[
                    { img: "search-ambulance.png", text: "Search for ambulance during crisis" },
                    { img: "emergency-plan.png", text: "No emergency plan" },
                    { img: "family-notinformed.png", text: "Family not informed" },
                    { img: "no-medical.png", text: "No medical information available" },
                    { img: "panic-confusion.png", text: <>Panic &amp; <br />confusion</> },
                  ].map(({ img, text }, i) => (
                    <div className="col-sm-6" key={i}>
                      <div className="icon-box">
                        <div className="icon">
                          <img src={`/services/icons/${img}`} alt="" />
                        </div>
                        <h4 className="info">{text}</h4>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* With Pococare */}
            <div className="col-md-6 why-col col-right">
              <div className="why-box with-poco">
                <h3 className="paraAnimation">With Pococare</h3>
                <div className="row gy-4">
                  {[
                    { img: "one-click-solution.png", text: <><b>One-click</b> assistance</> },
                    { img: "personalized-care.png", text: <>Personalised <b>Medical Emergency</b> Readiness Plan</> },
                    { img: "cordinated-communication.png", text: <><b>Coordinated</b> communication</> },
                    { img: "emergency-profile.png", text: <><b>Emergency</b> profile available</> },
                    { img: "guided-support.png", text: <><b>Guided</b> <br />support</> },
                  ].map(({ img, text }, i) => (
                    <div className="col-sm-6" key={i}>
                      <div className="icon-box">
                        <div className="icon">
                          <img src={`/services/icons/${img}`} alt="" />
                        </div>
                        <h4 className="info">{text}</h4>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── EMERGENCY HELP ── */}
      <section className="emergency-help">
        <div className="container">
          <div className="row gutter-lg">
            <div className="col-md-6">
              <div className="emergency-box not-included">
                <div className="img-wrap reveal-img">
                  <img src="/services/icons/medical-emergency.png" alt="" />
                </div>
                <div className="content">
                  <h2 className="paraAnimation">
                    Medical emergencies are <br />rarely planned.
                  </h2>
                  <p className="color-text-muted paraAnimation">
                    Most families struggle because:
                  </p>
                  <ul className="services-list">
                    <li>Critical medical information is unavailable</li>
                    <li>Emergency contacts are not reachable</li>
                    <li>Families do not know which hospital to choose</li>
                    <li>Ambulances take time to arrange</li>
                    <li>Panic delays decision making</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="emergency-box included">
                <div className="img-wrap reveal-img">
                  <img src="/services/icons/poco-medical-emergency.png" alt="" />
                </div>
                <div className="content">
                  <h2 className="paraAnimation">
                    Pococare helps you prepare before an emergency happens.
                  </h2>
                  <ul className="services-list">
                    <li>Create a Medical Emergency Readiness Profile</li>
                    <li>Store critical medical information securely</li>
                    <li>Build your family's Emergency Response Plan</li>
                    <li>Get access to 24x7 emergency assistance</li>
                    <li>Connect to qualified doctors</li>
                    <li>Ambulance coordination when needed</li>
                    <li>Support during the most stressful moments</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="how-works">
        <div className="container">
          <h2 className="global-heading paraAnimation">
            How It <b>Works?</b>
          </h2>
          <div className="row">
            {[
              { step: "step1", arrow: "step1-arrow", text: <>Choose Your <b>Plan</b></> },
              { step: "step2", arrow: "step2-arrow", text: <>Complete <b>Payment</b></> },
              { step: "step3", arrow: "step3-arrow", text: <>Create Your <b>Medical Emergency Readiness</b> Profile</> },
              { step: "step4", arrow: "step4-arrow", text: <>Download Your <b>QR Code, Mobile App &amp; Access Details</b></> },
              { step: "step5", arrow: null, text: <>Stay <b>Medical Emergency</b> Ready</> },
            ].map(({ step, arrow, text }, i) => (
              <div className="col-sm" key={i}>
                <div className="step-box">
                  <div className="step-img position-relative d-inline-block">
                    <img
                      src={`/services/${step}.png`}
                      className="steps bg-white"
                      alt=""
                    />
                    {arrow && (
                      <img
                        src={`/services/${arrow}.png`}
                        className="arrow"
                        alt=""
                      />
                    )}
                  </div>
                  <p className="info">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY FAMILIES CHOOSE ── */}
      <section className="why-choose">
        <div className="container">
          <h2 className="global-heading text-white paraAnimation">
            Why Families <b>Choose Pococare</b>
          </h2>
          <div className="row">
            {[
              { img: "cities-100+.png", text: <>1000+ Cities <br />Supported</> },
              { img: "emergency-24x7.png", text: <>24×7 Emergency Assistance</> },
              { img: "qualified-doctor.png", text: <>Qualified Doctor Network</> },
              { img: "ambulance-network.png", text: <>Coordinated Ambulance Network</> },
              { img: "personalized-planning.png", text: <>Personalised Emergency Readiness Planning</> },
              { img: "family-coverage.png", text: <>Family Coverage Options</> },
            ].map(({ img, text }, i) => (
              <div className="col-sm" key={i}>
                <div className="why-box">
                  <img src={`/services/icons/${img}`} alt="" />
                  <p className="info">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer-section">
        <div className="container">
          <div className="footer-inner">
            <div className="logos">
              <a href="#" className="pococare">
                <img src="https://pococare.com/wp-content/themes/pococare/assets/images/logo.png" alt="Pococare" />
              </a>
              <a href="#" className="partner">
                <img src={partner.logo} alt={partner.companyName} />
              </a>
            </div>
            <h6 className="fw-bold color-teal mb-3">Contact Us</h6>
            <div className="contact-info">
              <a
                target="_blank"
                rel="noreferrer"
                href="https://google.com/maps?gs_lcrp=EgZjaHJvbWUqDggAEEUYJxg7GIAEGIoFMg4IABBFGCcYOxiABBiKBTIQCAEQLhjHARixAxjRAxiABDIGCAIQRRg8MgYIAxBFGDwyBggEEEUYPDIGCAUQRRg8MgYIBhBFGDwyBggHEEUYPNIBCDExOTFqMGo3qAIAsAIA&um=1&ie=UTF-8&fb=1&gl=in&sa=X&geocode=KQX0WnIAE647McM27XkESQpx&daddr=1st+Floor,+VR+CHAMBERS,+26/2,+Outer+Ring+Rd,+Kadubeesanahalli,+Bengaluru,+Karnataka+560103"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="18">
                  <path d="M128 252.6C128 148.4 214 64 320 64C426 64 512 148.4 512 252.6C512 371.9 391.8 514.9 341.6 569.4C329.8 582.2 310.1 582.2 298.3 569.4C248.1 514.9 127.9 371.9 127.9 252.6zM320 320C355.3 320 384 291.3 384 256C384 220.7 355.3 192 320 192C284.7 192 256 220.7 256 256C256 291.3 284.7 320 320 320z" />
                </svg>
                Bengaluru, India
              </a>
              <a href="tel:+918056066766">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="16">
                  <path d="M224.2 89C216.3 70.1 195.7 60.1 176.1 65.4L170.6 66.9C106 84.5 50.8 147.1 66.9 223.3C104 398.3 241.7 536 416.7 573.1C493 589.3 555.5 534 573.1 469.4L574.6 463.9C580 444.2 569.9 423.6 551.1 415.8L453.8 375.3C437.3 368.4 418.2 373.2 406.8 387.1L368.2 434.3C297.9 399.4 241.3 341 208.8 269.3L253 233.3C266.9 222 271.6 202.9 264.8 186.3L224.2 89z" />
                </svg>
                +91 80560 66766
              </a>
              <a href="mailto:info@pococare.com">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="17">
                  <path d="M112 128C85.5 128 64 149.5 64 176C64 191.1 71.1 205.3 83.2 214.4L291.2 370.4C308.3 383.2 331.7 383.2 348.8 370.4L556.8 214.4C568.9 205.3 576 191.1 576 176C576 149.5 554.5 128 528 128L112 128zM64 260L64 448C64 483.3 92.7 512 128 512L512 512C547.3 512 576 483.3 576 448L576 260L377.6 408.8C343.5 434.4 296.5 434.4 262.4 408.8L64 260z" />
                </svg>
                info@pococare.com
              </a>
            </div>
            <ul className="social-links">
              <li>
                <a href="https://www.instagram.com/pococareofficial/" target="_blank" rel="noreferrer">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="25">
                    <path d="M320.3 205C256.8 204.8 205.2 256.2 205 319.7C204.8 383.2 256.2 434.8 319.7 435C383.2 435.2 434.8 383.8 435 320.3C435.2 256.8 383.8 205.2 320.3 205zM319.7 245.4C360.9 245.2 394.4 278.5 394.6 319.7C394.8 360.9 361.5 394.4 320.3 394.6C279.1 394.8 245.6 361.5 245.4 320.3C245.2 279.1 278.5 245.6 319.7 245.4zM413.1 200.3C413.1 185.5 425.1 173.5 439.9 173.5C454.7 173.5 466.7 185.5 466.7 200.3C466.7 215.1 454.7 227.1 439.9 227.1C425.1 227.1 413.1 215.1 413.1 200.3zM542.8 227.5C541.1 191.6 532.9 159.8 506.6 133.6C480.4 107.4 448.6 99.2 412.7 97.4C375.7 95.3 264.8 95.3 227.8 97.4C192 99.1 160.2 107.3 133.9 133.5C107.6 159.7 99.5 191.5 97.7 227.4C95.6 264.4 95.6 375.3 97.7 412.3C99.4 448.2 107.6 480 133.9 506.2C160.2 532.4 191.9 540.6 227.8 542.4C264.8 544.5 375.7 544.5 412.7 542.4C448.6 540.7 480.4 532.5 506.6 506.2C532.8 480 541 448.2 542.8 412.3C544.9 375.3 544.9 264.5 542.8 227.5zM495 452C487.2 471.6 472.1 486.7 452.4 494.6C422.9 506.3 352.9 503.6 320.3 503.6C287.7 503.6 217.6 506.2 188.2 494.6C168.6 486.8 153.5 471.7 145.6 452C133.9 422.5 136.6 352.5 136.6 319.9C136.6 287.3 134 217.2 145.6 187.8C153.4 168.2 168.5 153.1 188.2 145.2C217.7 133.5 287.7 136.2 320.3 136.2C352.9 136.2 423 133.6 452.4 145.2C472 153 487.1 168.1 495 187.8C506.7 217.3 504 287.3 504 319.9C504 352.5 506.7 422.6 495 452z" />
                  </svg>
                </a>
              </li>
              <li>
                <a href="https://www.linkedin.com/company/pococare/" target="_blank" rel="noreferrer">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="25">
                    <path d="M196.3 512L103.4 512L103.4 212.9L196.3 212.9L196.3 512zM149.8 172.1C120.1 172.1 96 147.5 96 117.8C96 103.5 101.7 89.9 111.8 79.8C121.9 69.7 135.6 64 149.8 64C164 64 177.7 69.7 187.8 79.8C197.9 89.9 203.6 103.6 203.6 117.8C203.6 147.5 179.5 172.1 149.8 172.1zM543.9 512L451.2 512L451.2 366.4C451.2 331.7 450.5 287.2 402.9 287.2C354.6 287.2 347.2 324.9 347.2 363.9L347.2 512L254.4 512L254.4 212.9L343.5 212.9L343.5 253.7L344.8 253.7C357.2 230.2 387.5 205.4 432.7 205.4C526.7 205.4 544 267.3 544 347.7L544 512L543.9 512z" />
                  </svg>
                </a>
              </li>
            </ul>
            <div className="appstore">
              <a href="#">
                <img src="/services/google-play.jpg" alt="Google Play" />
              </a>
              <a href="#">
                <img src="/services/app-store.jpg" alt="App Store" />
              </a>
            </div>
            <div className="copyrights">© 2026 Copyright POCOCARE. All Rights Reserved</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServicesLanding;
